import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Star, Clock, MapPin, ShieldCheck, Stethoscope, Baby, HeartPulse, Brain, Activity } from 'lucide-react';
import { professionals } from '../../data/mockData';
import { useAppContext } from '../../contexts/AppContext';
export function BookDoctorFlow() {
  const navigate = useNavigate();
  const {
    setSelectedProfessional
  } = useAppContext();
  const [step, setStep] = useState(1);
  const [specialty, setSpecialty] = useState('');
  const [loading, setLoading] = useState(false);
  const specialties = [{
    id: 'General Doctor',
    icon: Stethoscope,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }, {
    id: 'Pediatrics',
    icon: Baby,
    color: 'text-pink-600',
    bg: 'bg-pink-100'
  }, {
    id: 'Gynecology',
    icon: HeartPulse,
    color: 'text-purple-600',
    bg: 'bg-purple-100'
  }, {
    id: 'Cardiology',
    icon: Activity,
    color: 'text-red-600',
    bg: 'bg-red-100'
  }, {
    id: 'Dermatology',
    icon: Search,
    color: 'text-teal-600',
    bg: 'bg-teal-100'
  }, {
    id: 'Mental Health',
    icon: Brain,
    color: 'text-indigo-600',
    bg: 'bg-indigo-100'
  }];
  const handleSelectSpecialty = (spec: string) => {
    setSpecialty(spec);
    setStep(2);
    setLoading(true);
    setTimeout(() => setLoading(false), 1000);
  };
  const handleSelectDoctor = (doctor: any) => {
    setSelectedProfessional(doctor);
    navigate(`/profile/${doctor.id}`);
  };
  const filteredDoctors = professionals.filter(p => p.specialty.toLowerCase().includes(specialty.toLowerCase()) || specialty === 'General Doctor' && p.specialty === 'General Doctor');
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-415">
      <div className="bg-white px-6 pt-12 pb-4 shadow-sm sticky top-0 z-10" data-id="element-416">
        <div className="flex items-center gap-3 mb-4" data-id="element-417">
          <button onClick={() => step > 1 ? setStep(1) : navigate(-1)} className="p-2 -ml-2 hover:bg-slate-50 rounded-full" data-id="element-418">
            <ArrowLeft className="w-6 h-6 text-slate-700" data-id="element-419" />
          </button>
          <h1 className="text-xl font-bold text-slate-900" data-id="element-420">Book a Doctor</h1>
        </div>
        <div className="flex gap-2" data-id="element-421">
          {[1, 2].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-blue-600' : 'bg-slate-200'}`} data-id="element-422" />)}
        </div>
      </div>

      <div className="p-6 flex-1 overflow-y-auto" data-id="element-423">
        {step === 1 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-424">
            <h2 className="font-semibold text-slate-900 mb-2" data-id="element-425">
              Select Specialty
            </h2>
            <div className="grid grid-cols-2 gap-4" data-id="element-426">
              {specialties.map(spec => <button key={spec.id} onClick={() => handleSelectSpecialty(spec.id)} className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center text-center active:scale-95 transition-transform" data-id="element-427">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${spec.bg}`} data-id="element-428">
                    <spec.icon className={`w-7 h-7 ${spec.color}`} data-id="element-429" />
                  </div>
                  <span className="font-semibold text-slate-900 text-sm" data-id="element-430">
                    {spec.id}
                  </span>
                </button>)}
            </div>
          </motion.div>}

        {step === 2 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-431">
            <h2 className="font-semibold text-slate-900 mb-2" data-id="element-432">
              {specialty}s Near You
            </h2>

            {loading ? <div className="space-y-4" data-id="element-433">
                {[1, 2, 3].map(i => <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 animate-pulse flex gap-4" data-id="element-434">
                    <div className="w-20 h-20 bg-slate-200 rounded-xl shrink-0" data-id="element-435" />
                    <div className="flex-1 space-y-2 py-1" data-id="element-436">
                      <div className="h-4 bg-slate-200 rounded w-3/4" data-id="element-437" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" data-id="element-438" />
                      <div className="h-3 bg-slate-200 rounded w-1/4 mt-4" data-id="element-439" />
                    </div>
                  </div>)}
              </div> : filteredDoctors.length > 0 ? filteredDoctors.map(doc => <div key={doc.id} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" data-id="element-440">
                  <div className="flex space-x-4" data-id="element-441">
                    <img src={doc.image} alt={doc.name} className="w-20 h-20 rounded-xl object-cover" data-id="element-442" />
                    <div className="flex-1" data-id="element-443">
                      <div className="flex items-center" data-id="element-444">
                        <h3 className="font-bold text-slate-900 text-base" data-id="element-445">
                          {doc.name}
                        </h3>
                        {doc.verified && <ShieldCheck size={16} className="text-blue-600 ml-1" data-id="element-446" />}
                      </div>
                      <p className="text-sm text-slate-500" data-id="element-447">{doc.specialty}</p>

                      <div className="flex items-center mt-2 space-x-3" data-id="element-448">
                        <div className="flex items-center" data-id="element-449">
                          <Star size={14} className="text-amber-400 fill-current" data-id="element-450" />
                          <span className="text-xs font-medium ml-1" data-id="element-451">
                            {doc.rating}
                          </span>
                        </div>
                        <div className="flex items-center text-slate-500" data-id="element-452">
                          <MapPin size={14} className="mr-1" data-id="element-453" />
                          <span className="text-xs" data-id="element-454">{doc.distance}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between" data-id="element-455">
                    <div className="flex flex-col" data-id="element-456">
                      <span className="text-xs text-slate-500 mb-1 flex items-center" data-id="element-457">
                        <Clock size={12} className="mr-1" data-id="element-458" /> {doc.nextAvailable}
                      </span>
                      <span className="text-sm font-bold text-blue-600" data-id="element-459">
                        ₦{doc.fee.toLocaleString()}
                      </span>
                    </div>
                    <button onClick={() => handleSelectDoctor(doc)} className="bg-blue-600 text-white text-sm font-semibold px-5 py-2 rounded-xl active:bg-blue-700 transition-colors" data-id="element-460">
                      Book Now
                    </button>
                  </div>
                </div>) : <div className="text-center py-12" data-id="element-461">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-462">
                  <Search className="w-8 h-8 text-slate-400" data-id="element-463" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-464">
                  No doctors found
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-465">
                  We couldn't find any {specialty}s nearby right now.
                </p>
              </div>}
          </motion.div>}
      </div>
    </div>;
}